import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextFieldProps,
} from "@mui/material";
import * as React from "react";
import { VipTagConfig } from "../types";

/**
 * Colored dot indicator
 */
const ColorDot: React.FC<{ color: string }> = ({ color }) => (
  <Box
    sx={{
      width: 12,
      height: 12,
      borderRadius: "50%",
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

interface VipTagSelectProps {
  value: string[];
  availableTags: VipTagConfig[];
  onChange: (tagIds: string[]) => void;
  /** @deprecated */
  textFieldProps?: Partial<TextFieldProps>;
  disabled?: boolean;
  /** @deprecated */
  label?: string;
}

/**
 * Multi-select dropdown for VIP tags with checkboxes.
 */
export const VipTagSelect: React.FC<VipTagSelectProps> = ({
  value,
  availableTags,
  onChange,
  disabled = false,
}) => {
  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const newValue = event.target.value;
    onChange(typeof newValue === "string" ? newValue.split(",") : newValue);
  };

  // Get tag config by ID
  const getTag = (id: string) => availableTags.find((t) => t.id === id);

  return (
    <FormControl size="small" sx={{ minWidth: 120 }}>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        disabled={disabled}
        displayEmpty
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <Box sx={{ color: "text.secondary" }}>Tags</Box>
            );
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((id) => {
                const tag = getTag(id);
                if (!tag) return null;
                return (
                  <Chip
                    key={id}
                    label={tag.tag_label}
                    size="small"
                    sx={{
                      height: 20,
                      bgcolor: `${tag.tag_color}20`,
                      borderColor: tag.tag_color,
                      color: tag.tag_color,
                      fontWeight: 500,
                      "& .MuiChip-label": { px: 1 },
                    }}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          );
        }}
        MenuProps={{
          sx: { zIndex: 10000 },
        }}
        sx={{
          "& .MuiSelect-select": {
            py: 0.75,
          },
        }}
      >
        {availableTags.map((tag) => (
          <MenuItem key={tag.id} value={tag.id} sx={{ py: 0.5, gap: 1 }}>
            <Checkbox
              checked={value.includes(tag.id)}
              size="small"
              sx={{ p: 0 }}
            />
            <ColorDot color={tag.tag_color} />
            <ListItemText primary={tag.tag_label} sx={{ m: 0 }} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
